'use client';

import React, { useState, useEffect, useRef } from 'react';
import { imageCategories } from './ImageCategorySelector';
import { Play, Pause, SkipBack, SkipForward, Download, X, Maximize } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getMonitoringImageBlob, getMonitoringImageNames } from '../api/data';
import { getMonitoringLatestImageName } from '../api/data';

const ImageViewer = ({ onError, onLoading }) => {

    const [images, setImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [currentImageURL, setCurrentImageURL] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAutoPlay, setIsAutoPlay] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false); //

    // Fullscreen zoom and pan states
    const [scale, setScale] = useState(1);
    const [translateX, setTranslateX] = useState(0);
    const [translateY, setTranslateY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [lastTouchDistance, setLastTouchDistance] = useState(0);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [sliderValue, setSliderValue] = useState(0);

    const imageRef = useRef(null);
    const containerRef = useRef(null);
    const fullscreenRef = useRef(null);
    const autoPlayIntervalRef = useRef(null);
    const timerRef = useRef(null);

    const mode = useSelector(state => state.app.monitoringMode) ?? "live";
    const category = useSelector(state => state.app.selectedCategory) ?? "module";

    useEffect(() => {
        if (timerRef.current !== null) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        setCurrentImageIndex(0);

        if (mode == "live") {
            timerRef.current = window.setInterval(() => {
                fetchImages(true);
            }, 15000);
        } else {
            fetchImages(true)
        }

        return () => {
            if (timerRef.current !== null) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [mode, category]);

    // Fetch images for selected date
    useEffect(() => {
        fetchImages();
    }, [category]);

    // Auto-play functionality
    useEffect(() => {
        if (isAutoPlay && images.length > 1) {
            autoPlayIntervalRef.current = setInterval(() => {
                setCurrentImageIndex((prev) => (prev + 1) % images.length);
            }, 2000);
        } else {
            if (autoPlayIntervalRef.current) {
                clearInterval(autoPlayIntervalRef.current);
                autoPlayIntervalRef.current = null;
            }
        }

        return () => {
            if (autoPlayIntervalRef.current) {
                clearInterval(autoPlayIntervalRef.current);
            }
        };
    }, [isAutoPlay, images.length]);

    // Fullscreen keyboard controls
    useEffect(() => {
        const handleKeyDown = e => {
            if (!isFullScreen) return;

            switch (e.key) {
                case 'Escape':
                    handleCloseFullScreen();
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    handlePrevious();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    handleNext();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullScreen]);

    useEffect(() => {
        onLoading?.(isLoading)
    }, [isLoading])

    useEffect(() => {
        const fetchBlobURL = async () => {
            try {
                const currentImageName = images[currentImageIndex];
                if (!currentImageName) return;

                const imageBlob = await getMonitoringImageBlob(
                    getCatgoryGroup(),
                    currentImageName.filename
                );

                setCurrentImageURL(imageBlob.dataUri);
            } catch (err) {
                console.error("Failed to load image:", err);
                setCurrentImageURL(null);
            }
        };

        fetchBlobURL();
    }, [currentImageIndex]);

    function getCatgoryGroup() {
        switch (category) {
            case "shipping1": {
                return "line1"
            }
            case "shipping2": {
                return "line2"
            }
            case "module": {
                return "module"
            }
            case "hmgma": {
                return "HMGMA"
            }
            default:
                throw new Error("Unknown category: " + category);
        }
    }

    const fetchImages = async (isLatest = false) => {
        try {
            setIsLoading(true);
            setError(null);
            onError(null);

            // 공통: API 호출
            const imageNames = mode == 'live' ?  await getMonitoringLatestImageName(getCatgoryGroup()) : await getMonitoringImageNames(getCatgoryGroup())
            const resultImages = imageNames
                .map((name) => {
                    const m = name.match(/_(\d{4}-\d{2}-\d{2})_(\d{2})-(\d{2})-(\d{2})/);
                    const [, yyyyMMdd, HH, mm, ss] = m;
                    const iso = `${yyyyMMdd}T${HH}:${mm}:${ss}`; // 2025-08-24T17:07:39

                    return {
                        filename: name,
                        timestamp: iso,
                    };
                })
                .sort((a, b) => (b.timestamp ?? "").localeCompare(a.timestamp ?? ""));

            setImages(resultImages);

            if (!isLatest) {
                setCurrentImageIndex(0);
            } else {
                if (currentImageIndex >= resultImages.length && resultImages.length > 0) {
                    setCurrentImageIndex(0);
                }
            }

            let imageBlob = await getMonitoringImageBlob(
                getCatgoryGroup(),
                resultImages[currentImageIndex].filename
            );

            setCurrentImageURL(imageBlob.dataUri);
        } catch (err) {
            setError(`Failed to load images: ${err instanceof Error ? err.message : 'Unknown error'}`);
            onError?.(err)
        } finally {
            setIsLoading(false);
        }
    };

    const handlePrevious = () => {
        setCurrentImageIndex(prev => prev > 0 ? prev - 1 : images.length - 1);
    };

    const handleNext = () => {
        setCurrentImageIndex(prev => (prev + 1) % images.length);
    };

    const handleDownload = async () => {
        const currentImage = images[currentImageIndex]
        if (currentImage) {
            const link = document.createElement('a');
            link.href = currentImageURL;
            link.download = currentImage.filename;
            link.click();
        }
    };

    const handleOpenFullScreen = () => {
        setIsFullScreen(true);
        setScale(1);
        setTranslateX(0);
        setTranslateY(0);
        // Prevent body scroll when fullscreen is open
        document.body.style.overflow = 'hidden';
    };

    const handleCloseFullScreen = () => {
        setIsFullScreen(false);
        setScale(1);
        setTranslateX(0);
        setTranslateY(0);
        setIsDragging(false);
        // Restore body scroll
        document.body.style.overflow = '';
    };

    // Touch/Mouse event handlers for fullscreen zoom and pan
    const getTouchDistance = touches => {
        if (touches.length < 2) return 0;
        const touch1 = touches[0];
        const touch2 = touches[1];
        return Math.sqrt(
            Math.pow(touch2.clientX - touch1.clientX, 2) +
            Math.pow(touch2.clientY - touch1.clientY, 2)
        );
    };

    const handleTouchStart = e => {
        if (!isFullScreen) return;

        if (e.touches.length === 1) {
            // Single touch - start dragging (allow panning even at 1x zoom for better UX)
            setIsDragging(true);
            setDragStart({
                x: e.touches[0].clientX - translateX,
                y: e.touches[0].clientY - translateY
            });
        } else if (e.touches.length === 2) {
            // Two touches - start pinch zoom
            const distance = getTouchDistance(e.touches);
            setLastTouchDistance(distance);
            setIsDragging(false);
        }
    };

    const handleTouchMove = e => {
        if (!isFullScreen) return;

        e.preventDefault();

        if (e.touches.length === 1 && isDragging) {
            // Single touch - pan the image (allow panning at any zoom level)
            const newTranslateX = e.touches[0].clientX - dragStart.x;
            const newTranslateY = e.touches[0].clientY - dragStart.y;
            setTranslateX(newTranslateX);
            setTranslateY(newTranslateY);
        } else if (e.touches.length === 2) {
            // Two touches - pinch zoom
            const distance = getTouchDistance(e.touches);
            if (lastTouchDistance > 0) {
                const scaleChange = distance / lastTouchDistance;
                const newScale = Math.min(Math.max(scale * scaleChange, 0.25), 4);
                setScale(newScale);

                // If zooming out to 1x or below, reset position
                if (newScale <= 1) {
                    setTranslateX(0);
                    setTranslateY(0);
                }
            }
            setLastTouchDistance(distance);
        }
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
        setLastTouchDistance(0);
    };

    // Mouse event handlers for desktop
    const handleMouseDown = e => {
        if (!isFullScreen) return;

        setIsDragging(true);
        setDragStart({
            x: e.clientX - translateX,
            y: e.clientY - translateY
        });
    };

    const handleMouseMove = e => {
        if (!isFullScreen || !isDragging) return;

        const newTranslateX = e.clientX - dragStart.x;
        const newTranslateY = e.clientY - dragStart.y;
        setTranslateX(newTranslateX);
        setTranslateY(newTranslateY);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Wheel event for desktop zoom
    const handleWheel = e => {
        if (!isFullScreen) return;

        e.preventDefault();
        const scaleChange = e.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.min(Math.max(scale * scaleChange, 0.25), 4);
        setScale(newScale);

        // If zooming out to 1x or below, reset position
        if (newScale <= 1) {
            setTranslateX(0);
            setTranslateY(0);
        }
    };

    const currentImage = images[currentImageIndex];

    // Live로 이미지 바뀔 때 깜빡임 없애기 위해 timeline일 때만 로딩 표시
    if (mode == 'timesline' && isLoading) {
        return (
            <div className="image-viewer-container flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Loading images...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="image-viewer-container flex items-center justify-center">
                <div className="text-center">
                    <p className="text-danger-600 mb-2">{error}</p>
                    <button
                        onClick={fetchImages}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (images.length === 0) {
        return (
            <div className="image-viewer-container flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-600">No images available for this date</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-4">
                {/* <SimpleSecureGallery/> */}
                {/* Image Container */}
                <div className="image-viewer-container" ref={containerRef}>
                    <div className="w-full h-full overflow-auto">
                        <img
                            ref={imageRef}
                            src={currentImageURL}
                            alt={`Production image ${currentImageIndex + 1}`}
                            className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={handleOpenFullScreen}
                            onError={() => setError('Failed to load image')}
                        />
                    </div>

                    {/* Image Info Overlay */}
                    <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
                        {new Date(currentImage.timestamp).toLocaleString()}
                    </div>

                    {/* Fullscreen Button */}
                    <button
                        onClick={handleOpenFullScreen}
                        className="absolute top-0.5 right-2 bg-black bg-opacity-70 text-white p-2 rounded-lg hover:bg-opacity-90 transition-all"
                        title="Open in fullscreen"
                    >
                        <Maximize className="h-4 w-4" />
                    </button>

                    {/* Controls Overlay */}
                    <div className="absolute bottom-1 left-2 right-2">
                        {/* Download Button */}
                        <div className="flex items-center justify-center space-x-2">
                            <div className="bg-black bg-opacity-70 rounded-lg px-2 py-1 flex items-center space-x-1">
                                <button
                                    onClick={handleDownload}
                                    className="text-white hover:text-primary-300 p-1"
                                >
                                    <Download className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Timeline Controls */}
                        {mode == 'timeline' && images.length > 1 && (
                            <div className="bg-black bg-opacity-70 rounded-lg p-2">
                                <div className="flex items-center space-x-2 mb-2">
                                    <button
                                        onClick={handlePrevious}
                                        className="text-white hover:text-primary-300 p-1"
                                    >
                                        <SkipBack className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => setIsAutoPlay(!isAutoPlay)}
                                        className="text-white hover:text-primary-300 p-1"
                                    >
                                        {isAutoPlay ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                    </button>
                                    <button
                                        onClick={handleNext}
                                        className="text-white hover:text-primary-300 p-1"
                                    >
                                        <SkipForward className="h-4 w-4" />
                                    </button>
                                    <span className="text-white text-xs">
                                        {currentImageIndex + 1} / {images.length}
                                    </span>
                                </div>

                                {/* Timeline Slider */}
                                <input
                                    type="range"
                                    min="0"
                                    max={images.length - 1}
                                    value={sliderValue}
                                    onChange={(e) => {
                                        setSliderValue(parseInt(e.target.value))
                                    }}
                                    onMouseUp={() => {
                                        setCurrentImageIndex(sliderValue)
                                    }}
                                    onTouchEnd={() => {
                                        setCurrentImageIndex(sliderValue)
                                    }}
                                    className="timeline-control w-full"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Fullscreen Modal */}
            {isFullScreen && (
                <div className="fixed inset-0 z-50 bg-black">
                    <div
                        ref={fullscreenRef}
                        className="w-full h-full flex items-center justify-center overflow-hidden"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onWheel={handleWheel}
                    >
                        <img
                            src={currentImageURL}
                            alt={`Production image ${currentImageIndex + 1}`}
                            className="w-full h-auto  select-none"
                            style={{
                                transform: `translate(${translateX}px, ${translateY}px) rotate(90deg) scale(${scale * 1.4})`,
                                transformOrigin: 'center',
                                cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                                touchAction: 'none',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                            }}
                            draggable={false}
                            onError={() => setError('Failed to load image')}
                        />
                    </div>

                    {/* Fullscreen Controls */}
                    <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
                        {/* Image Info */}
                        <div className="bg-black bg-opacity-70 text-white px-3 py-2 rounded-lg text-sm">
                            <div>{new Date(currentImage.timestamp).toLocaleString()}</div>
                            {scale > 1 && (
                                <div className="text-xs opacity-80 mt-1">
                                    Zoom: {Math.round(scale * 100)}% • Pinch to zoom • Drag to pan
                                </div>
                            )}
                        </div>

                        {/* Close Button */}
                        <button
                            onClick={handleCloseFullScreen}
                            className="bg-black bg-opacity-70 text-white p-2 rounded-lg hover:bg-opacity-90 transition-all"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Bottom Controls */}
                    <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center justify-center space-x-4">
                            {/* Navigation and Controls */}
                            <div className="bg-black bg-opacity-70 rounded-lg p-3 flex items-center space-x-3">
                                {mode == 'timeline' && images.length > 1 && (
                                    <>
                                        <button
                                            onClick={handlePrevious}
                                            className="text-white hover:text-primary-300 p-1"
                                        >
                                            <SkipBack className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => setIsAutoPlay(!isAutoPlay)}
                                            className="text-white hover:text-primary-300 p-1"
                                        >
                                            {isAutoPlay ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                                        </button>
                                        <button
                                            onClick={handleNext}
                                            className="text-white hover:text-primary-300 p-1"
                                        >
                                            <SkipForward className="h-5 w-5" />
                                        </button>
                                        <span className="text-white text-sm px-2">
                                            {currentImageIndex + 1} / {images.length}
                                        </span>
                                    </>
                                )}

                                <button
                                    onClick={handleDownload}
                                    className="text-white hover:text-primary-300 p-1"
                                >
                                    <Download className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        {/* Timeline Slider for fullscreen */}
                        {mode == 'timeline' && images.length > 1 && (
                            <div className="mt-3 px-4">
                                <input
                                    type="range"
                                    min="0"
                                    max={images.length - 1}
                                    value={sliderValue}
                                    onChange={(e) => {
                                        setSliderValue(parseInt(e.target.value))
                                    }}
                                    onMouseUp={() => {
                                        setCurrentImageIndex(sliderValue)
                                    }}
                                    onTouchEnd={() => {
                                        setCurrentImageIndex(sliderValue)
                                    }}
                                    className="timeline-control w-full"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ImageViewer;